// @ts-nocheck
/**
 * ContinuousEvalTab — Run list, detail, and report viewer for continuous evaluation.
 * Migrated from liukp/eval-demo-pages branch, refactored to use evalApi and CSS classes.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { evalApi } from "../../../client/EvalApiClient";
import type { HistogramDataPoint } from "../../../client/EvalApiClient";
import type { EvalRun } from "../../../client/EvalApiClient";
import "./ContinuousEvalTab.css";
import { RadarChart, scoreColor } from "../../../components/RadarChart";
import { loadErrorText } from "../../../utils/loadError";
import AiReviewSection from "./AiReviewSection";
import AskEvalAgent, { askValue } from "../../../components/AskEvalAgent/AskEvalAgent";
import { DEFAULT_EVAL_AGENT, evalAgentLabel } from "../../../agents";
import Table from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import Pagination from "@amzn/awsui-components-react/polaris/pagination";
import TextFilter from "@amzn/awsui-components-react/polaris/text-filter";
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Link from "@amzn/awsui-components-react/polaris/link";
import Button from "@amzn/awsui-components-react/polaris/button";
import CollectionPreferences from "@amzn/awsui-components-react/polaris/collection-preferences";
import { useCollection } from "@amzn/awsui-collection-hooks";


// ─── Inline SVG Icons ───────────────────────────────────────────────────────

function IconSpinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="eval-reports-continuous__spinner">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

// ─── Helper Components ──────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const key = status?.toUpperCase().replace(/ /g, "-") || "default";
  let modifier = "default";
  if (key === "SUCCEEDED") modifier = "succeeded";
  else if (key === "FAILED") modifier = "failed";
  else if (key === "IN_PROGRESS" || key === "IN-PROGRESS") modifier = "in-progress";

  return (
    <span className={`eval-reports-continuous__status eval-reports-continuous__status--${modifier}`}>
      {status}
    </span>
  );
}

function ScoreBadge({ score, threshold }: { score: number | string; threshold?: number | string }) {
  const { t } = useTranslation("eval");
  const num = typeof score === "string" ? parseFloat(score) : score;
  const thr = typeof threshold === "string" ? parseFloat(threshold) : threshold;
  if (isNaN(num) || num === null || num === undefined) {
    return <span className="eval-reports-continuous__score eval-reports-continuous__score--none">&mdash;</span>;
  }
  let modifier = "low";
  if (num >= (thr ?? 0.7)) modifier = "high";
  else if (num >= 0.5) modifier = "mid";

  return (
    <span className="eval-reports-continuous__rate-cell">
      <span className={`eval-reports-continuous__rate eval-reports-continuous__rate--${modifier}`}>
        {num.toFixed(2)}
      </span>
      <span className="eval-reports-continuous__rate-bar">
        <span
          className={`eval-reports-continuous__rate-bar-fill eval-reports-continuous__rate-bar-fill--${modifier}`}
          style={{ width: `${Math.min(num * 100, 100)}%` }}
        />
        {thr != null && !isNaN(thr) && (
          <span
            className="eval-reports-continuous__rate-bar-tick"
            style={{ left: `${Math.min(thr * 100, 100)}%` }}
            data-label={t("continuous.threshold", { value: thr.toFixed(2) })}
          />
        )}
      </span>
    </span>
  );
}


/** A metric's own pass threshold from the run's evaluator_summary entry. Recorded per
 *  run at evaluation time (UES), so a later config change never re-labels an old run.
 *  Old-pipeline runs have none: null. */
export function metricThreshold(data: { threshold?: unknown } | null | undefined): number | null {
  const raw = data?.threshold;
  const thr = typeof raw === "string" ? parseFloat(raw) : typeof raw === "number" ? raw : NaN;
  return Number.isFinite(thr) && thr >= 0 && thr <= 1 ? thr : null;
}

/** Colour of a metric's average: against its own threshold when known (pass = green,
 *  fail = red); otherwise the historical fixed bands (0.7 / 0.5). */
export function metricAvgColor(avg: number, threshold: number | null): string {
  return scoreColor(avg, threshold ?? undefined);
}

/** Each metric's own threshold for the run (metrics without one are left out). */
export function metricThresholds(summary: Record<string, any> | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [name, data] of Object.entries(summary || {})) {
    const thr = metricThreshold(data);
    if (thr != null) out[name] = thr;
  }
  return out;
}

function PassRateCircle({ rate, threshold }: { rate?: number | string; threshold?: number | string }) {
  const num = typeof rate === "string" ? parseFloat(rate) : rate;
  const thr = typeof threshold === "string" ? parseFloat(threshold) : threshold;
  if (num == null || isNaN(num)) {
    return <span className="eval-reports-continuous__score eval-reports-continuous__score--none">&mdash;</span>;
  }
  const pct = Math.round(num * 100);
  const pass = pct >= (thr != null ? thr * 100 : 70);
  const color = pass ? "#16a34a" : "#dc2626";
  const trackColor = pass ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.12)";
  const size = 52;
  const r = 20;
  const stroke = 4.5;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - num);
  const center = size / 2;
  return (
    <div className="eval-reports-continuous__pass-circle">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={center} cy={center} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle cx={center} cy={center} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform={`rotate(-90 ${center} ${center})`}
          style={{transition: "stroke-dashoffset 0.6s ease"}} />
      </svg>
      <div className="eval-reports-continuous__pass-circle-label">
        <span className="eval-reports-continuous__pass-circle-pct" style={{color}}>{pct}</span>
      </div>
    </div>
  );
}

function MetricTag({ name }: { name: string }) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(`/eval/evaluators?search=${encodeURIComponent(name)}`, "_blank");
  };
  return (
    <span
      className="eval-reports-continuous__metric-tag eval-reports-continuous__metric-tag--link"
      onClick={handleClick}
      title={`View "${name}" evaluator details`}
    >
      {name}
    </span>
  );
}

function MetricsCell({ metrics }: { metrics: string }) {
  const [expanded, setExpanded] = useState(false);
  if (!metrics) return <span>&mdash;</span>;

  const items = metrics.split(",").map((m) => m.trim().replace("Builtin.", ""));
  const MAX_VISIBLE = 1;
  const hasMore = items.length > MAX_VISIBLE;

  return (
    <div className="eval-reports-continuous__metrics-cell">
      <MetricTag name={items[0]} />
      {hasMore && (
        <span
          className="eval-reports-continuous__metric-tag eval-reports-continuous__metric-tag--more"
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        >
          {expanded ? "−" : `+${items.length - MAX_VISIBLE}`}
        </span>
      )}
      {expanded && (
        <div className="eval-reports-continuous__metrics-dropdown">
          {items.slice(MAX_VISIBLE).map((m) => (
            <MetricTag key={m} name={m} />
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Conversation Trace (tool call steps) ───────────────────────────────────

export function ConversationTrace({ messages }: { messages: any[] }) {
  const { t } = useTranslation("eval");
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (key: string) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Group messages by conversation_id to show all turns
  const turnMap: Record<string, any[]> = {};
  const turnOrder: string[] = [];
  for (const msg of messages) {
    const cid = msg.conversation_id || "_default";
    if (!turnMap[cid]) {
      turnMap[cid] = [];
      turnOrder.push(cid);
    }
    turnMap[cid].push(msg);
  }
  const turns = turnOrder.map(cid => ({ id: cid, messages: turnMap[cid] }));

  const totalTools = messages.filter(m => m.message_type === "tool_use").length;

  return (
    <>
      <h4 style={{margin:"16px 0 12px",fontSize:"14px",color:"var(--eval-text-primary)",display:"flex",alignItems:"center",gap:"8px"}}>
        {t("continuous.conversation")}
        <span style={{fontSize:"11px",color:"var(--eval-text-secondary)",fontWeight:400}}>
          {t(turns.length === 1 ? "continuous.traceSummaryOne" : "continuous.traceSummaryMany", { turns: turns.length, tools: totalTools, messages: messages.length })}
        </span>
      </h4>
      <div className="eval-trace">
        {turns.map((turn, turnIdx) => {
          const userMsg = turn.messages.find(m => m.message_type === "user_query" || (m.message_role === "user" && m.message_type !== "tool_use"));
          const toolSteps = turn.messages.filter(m => m.message_type === "tool_use");
          const finalResponse = turn.messages.find(m => m.message_type === "agent_final_response");

          return (
            <div key={turn.id} style={{ marginBottom: turns.length > 1 ? 16 : 0, paddingBottom: turns.length > 1 && turnIdx < turns.length - 1 ? 16 : 0, borderBottom: turns.length > 1 && turnIdx < turns.length - 1 ? "1px solid var(--eval-border, #e5e7eb)" : "none" }}>
              {turns.length > 1 && (
                <div style={{fontSize:"10px",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5,color:"var(--eval-text-secondary)",marginBottom:8}}>
                  {t("continuous.turn", { n: turnIdx + 1 })}
                </div>
              )}

              {/* User Query */}
              {userMsg && (
                <div className="eval-trace__user">
                  <div className="eval-trace__user-label">
                    <span className="eval-trace__user-icon">Q</span>
                    <span className="eval-trace__user-alias">{userMsg.user_alias || t("continuous.user")}</span>
                    {userMsg.create_time && <span className="eval-trace__time">{userMsg.create_time.slice(11, 19)}</span>}
                  </div>
                  <div className="eval-trace__user-content">{userMsg.message_content}</div>
                </div>
              )}

              {/* Tool Steps */}
              {toolSteps.length > 0 && (
                <div className="eval-trace__steps">
                  {toolSteps.map((step, idx) => {
                    const stepKey = `${turn.id}-${idx}`;
                    const isExpanded = expandedSteps.has(stepKey);
                    const toolName = step.message_content || "unknown_tool";
                    const toolInput = step.tool_input;
                    const toolResult = step.tool_result;
                    return (
                      <div key={stepKey} className={`eval-trace__step${isExpanded ? " eval-trace__step--expanded" : ""}`}>
                        <div className="eval-trace__step-header" onClick={() => toggleStep(stepKey)}>
                          <span className="eval-trace__step-num">{idx + 1}.</span>
                          <span className="eval-trace__step-name">{toolName}</span>
                          {step.create_time && <span className="eval-trace__time">{step.create_time.slice(11, 19)}</span>}
                          <span className="eval-trace__step-toggle">{isExpanded ? "▼" : "▶"}</span>
                        </div>
                        {isExpanded && (
                          <div className="eval-trace__step-body">
                            {toolInput && (
                              <div className="eval-trace__step-section">
                                <span className="eval-trace__step-label">{t("continuous.toolIn")}</span>
                                <pre className="eval-trace__step-code">
                                  {typeof toolInput === "string" ? toolInput : JSON.stringify(toolInput, null, 2)}
                                </pre>
                              </div>
                            )}
                            {toolResult && (
                              <div className="eval-trace__step-section">
                                <span className="eval-trace__step-label eval-trace__step-label--out">{t("continuous.toolOut")}</span>
                                <pre className="eval-trace__step-code eval-trace__step-code--out">
                                  {typeof toolResult === "string" ? toolResult : JSON.stringify(toolResult, null, 2)}
                                </pre>
                              </div>
                            )}
                            {!toolInput && !toolResult && (
                              <div className="eval-trace__step-section">
                                <span style={{fontSize:"11px",color:"var(--eval-text-secondary)",fontStyle:"italic"}}>{t("continuous.noToolIo")}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Final Response */}
              {finalResponse && (
                <div className="eval-trace__response">
                  <div className="eval-trace__response-label">
                    <span className="eval-trace__response-icon">A</span>
                    <span>{t("continuous.agentResponse")}</span>
                    {finalResponse.create_time && <span className="eval-trace__time">{finalResponse.create_time.slice(11, 19)}</span>}
                  </div>
                  <div className="eval-trace__response-content">{finalResponse.message_content}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── Histogram Chart ────────────────────────────────────────────────────────

function HistogramChart({ data, loading, question }: {
  data: HistogramDataPoint[];
  loading: boolean;
  question: string;
}) {
  const { t } = useTranslation("eval");

  const aggregated = data;

  const maxSessions = Math.max(...aggregated.map(d => d.sessions), 1);
  const maxEvals = Math.max(...aggregated.map(d => d.evaluations), 1);
  const maxVal = Math.max(maxSessions, maxEvals, 1);

  const totalSessions = data.reduce((sum, d) => sum + d.sessions, 0);
  const totalEvals = data.reduce((sum, d) => sum + d.evaluations, 0);

  return (
    <div className="eval-reports-continuous__histogram-card">
      <div className="eval-reports-continuous__histogram-header">
        <h3 className="eval-reports-continuous__histogram-title">{t("continuous.histogramTitle")}<AskEvalAgent question={question} /></h3>
        <div className="eval-reports-continuous__histogram-summary">
          <span className="eval-reports-continuous__histogram-legend eval-reports-continuous__histogram-legend--sessions">
            {t("continuous.sessionsLabel")} <strong>{totalSessions.toLocaleString()}</strong>
          </span>
          <span className="eval-reports-continuous__histogram-legend eval-reports-continuous__histogram-legend--evals">
            {t("continuous.evaluationsLabel")} <strong>{totalEvals.toLocaleString()}</strong>
          </span>
        </div>
      </div>
      {loading ? (
        <div className="eval-reports-continuous__histogram-loading">{t("continuous.loadingChart")}</div>
      ) : aggregated.length === 0 ? (
        <div className="eval-reports-continuous__histogram-empty">{t("continuous.noChartData")}</div>
      ) : (
        <div className="eval-reports-continuous__histogram-wrapper">
          <div className="eval-reports-continuous__histogram-yaxis">
            <span>{maxVal.toLocaleString()}</span>
            <span>{Math.round(maxVal / 2).toLocaleString()}</span>
            <span>0</span>
          </div>
          <div className="eval-reports-continuous__histogram-bars">
            {aggregated.map((d) => (
              <div key={d.date} className="eval-reports-continuous__histogram-group">
                <div className="eval-reports-continuous__histogram-bar-pair">
                  <div
                    className="eval-reports-continuous__histogram-bar eval-reports-continuous__histogram-bar--sessions"
                    style={{ height: `${(d.sessions / maxVal) * 100}%` }}
                    data-tooltip={t("continuous.sessionsBar", { date: d.date, n: d.sessions })}
                  />
                  <div
                    className="eval-reports-continuous__histogram-bar eval-reports-continuous__histogram-bar--evals"
                    style={{ height: `${(d.evaluations / maxVal) * 100}%` }}
                    data-tooltip={t("continuous.evaluationsBar", { date: d.date, n: d.evaluations })}
                  />
                </div>
                <span className="eval-reports-continuous__histogram-label">
                  {d.date.slice(5)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Main Tab Component ─────────────────────────────────────────────────────
export default function ContinuousEvalTab({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { t } = useTranslation("eval");
  // The agent selected in EvalLayout: runs, histogram and run details are its own.
  const agent = useOutletContext<{ agent?: string } | undefined>()?.agent;
  const agentLabel = evalAgentLabel(agent || DEFAULT_EVAL_AGENT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [runDetail, setRunDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionSort, setSessionSort] = useState<{field: string; dir: "asc"|"desc"}>({field: "", dir: "desc"});
  const [sessionFilter, setSessionFilter] = useState({ status: "", lowestMetric: "", minScore: "", maxScore: "", search: "" });
  const [sessionPage, setSessionPage] = useState(0);
  const SESSION_PAGE_SIZE = 10;
  const sessionsRef = useRef<HTMLDivElement>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [sessionDetail, setSessionDetail] = useState<any>(null);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  // Evaluations (flat list). The message is the server's, or a fallback key
  // translated at render (LoadError).
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [evaluationsLoading, setEvaluationsLoading] = useState(false);
  const [evaluationsMessage, setEvaluationsMessage] = useState(null);
  const [evalFilter, setEvalFilter] = useState({ evaluator: "", session_id: "", level: "", minScore: "", maxScore: "" });
  const [evalSort, setEvalSort] = useState<{field: string; dir: "asc"|"desc"}>({field: "", dir: "desc"});
  const [evalPage, setEvalPage] = useState(0);
  const EVAL_PAGE_SIZE = 10;
  const [expandedEvalIdx, setExpandedEvalIdx] = useState<number | null>(null);

  const [inlineReportUrl, setInlineReportUrl] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportExpanded, setReportExpanded] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  // Date range filter



  // Histogram state
  const [histogramData, setHistogramData] = useState<HistogramDataPoint[]>([]);
  const [histogramLoading, setHistogramLoading] = useState(true);

  // The latest load of each list wins: switching the agent (or the dates) requests again,
  // and a slower answer for the previous selection must not overwrite the current one.
  const histogramRequest = useRef(0);
  const loadHistogram = useCallback(async () => {
    const request = ++histogramRequest.current;
    const current = () => request === histogramRequest.current;
    setHistogramLoading(true);
    try {
      const data = await evalApi.getHistogram("30d", "all", startDate, endDate, agent);
      if (current()) setHistogramData(data.data || []);
    } catch (e: any) {
      console.error("[ContinuousEvalTab] Failed to load histogram:", e.message);
      if (current()) setHistogramData([]);
    } finally {
      if (current()) setHistogramLoading(false);
    }
  }, [startDate, endDate, agent]);

  useEffect(() => {
    loadHistogram();
  }, [loadHistogram]);
  // Derive view state from URL params
  const selectedRun = searchParams.get("run");
  const viewMode = searchParams.get("view"); // null = detail, "report" = iframe

  const runsRequest = useRef(0);
  const loadRuns = useCallback(async () => {
    const request = ++runsRequest.current;
    const current = () => request === runsRequest.current;
    try {
      setLoading(true);
      setError("");
      const data = await evalApi.listRuns(500, undefined, "continuous", startDate || undefined, endDate || undefined, agent);
      if (current()) setRuns(data.runs);
    } catch (e: any) {
      if (current()) setError(e.message);
    } finally {
      if (current()) setLoading(false);
    }
  }, [startDate, endDate, agent]);

  useEffect(() => {
    loadRuns();
  }, [startDate, endDate, agent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore view state from URL params on mount/refresh
  useEffect(() => {
    if (selectedRun && !runDetail && !detailLoading) {
      viewDetail(selectedRun);
      if (viewMode === "report") { setReportExpanded(true); }  // also handled below
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching the agent closes an open run (it is the previous agent's and may not
  // be the new one's): back to the list, like the Back button.
  const shownAgent = useRef(agent);
  useEffect(() => {
    if (shownAgent.current === agent) return;
    shownAgent.current = agent;
    if (selectedRun) { setRunDetail(null); setSessions([]); setSearchParams({}); }
  }, [agent]); // eslint-disable-line react-hooks/exhaustive-deps

  // When deep-linked with ?session=xxx, pre-fill session search filter and scroll
  useEffect(() => {
    const sessionParam = searchParams.get("session");
    if (sessionParam) {
      setSessionFilter((f) => ({ ...f, search: sessionParam }));
      if (sessions.length > 0 && sessionsRef.current) {
        sessionsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [sessions]); // re-apply when sessions load

  // When deep-linked with ?view=report, open full-page report once URL is ready
  useEffect(() => {
    if (viewMode === "report" && inlineReportUrl && !reportUrl) {
      setReportUrl(inlineReportUrl);
    }
  }, [viewMode, inlineReportUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewDetail = async (requestId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("run", requestId);
      next.delete("view");
      return next;
    });
    setRunDetail(null);
    setSessions([]);
    setInlineReportUrl(null);
    setReportExpanded(false);
    setDetailLoading(true);
    setSessionsLoading(true);

    // Parallel requests: metadata (fast) + sessions (slower)
    evalApi.getRunDetailOnly(requestId, agent)
      .then((data) => {
        setRunDetail(data);
      })
      .catch((e: any) => {
        setRunDetail({ error: e.message });
      })
      .finally(() => {
        setDetailLoading(false);
      });

    evalApi.getRunSessions(requestId, agent)
      .then((data) => {
        const sessionList = Array.isArray(data) ? data : (data as any).sessions || [];
        setSessions(sessionList);
      })
      .catch((e: any) => {
        console.error("[ContinuousEvalTab] Failed to load sessions:", e.message);
        setSessions([]);
      })
      .finally(() => {
        setSessionsLoading(false);
      });

    // Load report URL in background
    setInlineReportUrl(null);
    setReportExpanded(false);
    setReportLoading(true);
    evalApi.getReportUrl(requestId)
      .then((data) => {
        setInlineReportUrl(data.report_url || data.url || null);
      })
      .catch(() => {
        setInlineReportUrl(null);
      })
      .finally(() => {
        setReportLoading(false);
      });

    // Load evaluations
    setEvaluations([]);
    setEvaluationsLoading(true);
    setEvaluationsMessage(null);
    evalApi.getRunEvaluations(requestId, agent)
      .then((data: any) => {
        setEvaluations(data.evaluations || []);
        setEvaluationsMessage(data.message ? { message: data.message } : null);
      })
      .catch((e: any) => {
        console.error("[ContinuousEvalTab] Failed to load evaluations:", e.message);
        setEvaluations([]);
        setEvaluationsMessage({ key: "continuous.evaluationsFailed" });
      })
      .finally(() => {
        setEvaluationsLoading(false);
      });
  };

  const viewSessionDetail = async (sessionId: string) => {
    if (selectedSession === sessionId) {
      setSelectedSession(null);
      setSessionDetail(null);
      return;
    }
    setSelectedSession(sessionId);
    setSessionDetail(null);
    setSessionDetailLoading(true);
    try {
      const data = await evalApi.getSessionDetail(sessionId);
      setSessionDetail(data);
    } catch (e: any) {
      setSessionDetail({ error: e.message });
    } finally {
      setSessionDetailLoading(false);
    }
  };




  // ─── Cloudscape useCollection for run list table ───
  const RUN_PAGE_SIZE = 10;
  const RUN_PAGE_SIZE_OPTIONS = [
    { value: 10, label: t("continuous.pageSizeOption", { n: 10 }) },
    { value: 20, label: t("continuous.pageSizeOption", { n: 20 }) },
    { value: 50, label: t("continuous.pageSizeOption", { n: 50 }) },
  ];
  const [runTablePreferences, setRunTablePreferences] = useState({ pageSize: RUN_PAGE_SIZE });

  const { items: collectionItems, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(runs, {
    filtering: {
      filteringFunction: (item: any, filteringText: string) => {
        if (!filteringText) return true;
        const q = filteringText.toLowerCase();
        return item.request_id.toLowerCase().includes(q) || (item.status || "").toLowerCase().includes(q);
      },
    },
    pagination: { pageSize: runTablePreferences.pageSize },
    sorting: {
      defaultState: { sortingColumn: { sortingField: "created_at" }, isDescending: true },
    },
  });


  // ─── Full-page Report Viewer (iframe) ───
  if (reportUrl) {
    return (
      <div className="eval-reports-continuous__container">
        <button
          className="eval-reports-continuous__btn"
          onClick={() => { setReportUrl(null); }}
        >
          {t("continuous.backToDetail")}
        </button>
        <div className="eval-reports-continuous__iframe-wrapper">
          <iframe
            src={reportUrl}
            sandbox="allow-scripts allow-same-origin"
            className="eval-reports-continuous__iframe"
            title={t("continuous.reportFrame")}
          />
        </div>
      </div>
    );
  }

  // ─── Run Detail View ───
  if (selectedRun) {
    return (
      <div className="eval-reports-continuous__container">
        <button
          className="eval-reports-continuous__btn"
          onClick={() => { setRunDetail(null); setSearchParams({}); }}
        >
          {t("continuous.backToList")}
        </button>

        {detailLoading || !runDetail ? (
          <p className="eval-reports-continuous__loading">{t("continuous.loading")}</p>
        ) : runDetail.error ? (
          <p className="eval-reports-continuous__error">{runDetail.error}</p>
        ) : (
          <>
            <div className="eval-reports-continuous__card">
              <div className="eval-reports-continuous__detail-top">
                <h2 className="eval-reports-continuous__detail-header">
                  {t("continuous.runDetail")}
                  <AskEvalAgent question={t("askAgent.runSummary", {
                    agent: agentLabel,
                    runId: runDetail.request_id,
                    score: askValue(runDetail.score),
                    passRate: askValue(runDetail.pass_rate),
                    threshold: askValue(runDetail.pass_threshold),
                    sessions: runDetail.summary?.sessions || runDetail.summary?.unique_sessions || runDetail.summary?.total_sessions || 0,
                    evaluations: runDetail.summary?.total_evaluations || 0,
                  })} />
                </h2>
                {/* Hidden: remove display:none to show the button */}
                <button
                  className="eval-reports-continuous__btn eval-reports-continuous__btn--primary" style={{ display: "none" }}
                  onClick={() => {
                    if (inlineReportUrl) {
                      setReportUrl(inlineReportUrl);
                    }
                  }}
                  disabled={!inlineReportUrl && !reportLoading}
                >
                  {reportLoading ? t("continuous.loadingReportButton") : t("continuous.viewFullReport")}
                </button>
              </div>

              <div className="eval-reports-continuous__detail-id">
                <span className="eval-reports-continuous__detail-label">{t("continuous.runId")}</span>
                <code className="eval-reports-continuous__code" style={{marginLeft:"12px"}}>{runDetail.request_id}</code>
              </div>

              <div className="eval-reports-continuous__detail-grid eval-reports-continuous__detail-grid--6">
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.status")}</div>
                  <StatusBadge status={runDetail.status} />
                </div>
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.score")}</div>
                  <div className="eval-reports-continuous__detail-value">
                    <ScoreBadge score={runDetail.score || 0} threshold={runDetail.pass_threshold} />
                  </div>
                </div>
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.passThreshold")}</div>
                  <div className="eval-reports-continuous__detail-value">
                    {runDetail.pass_threshold
                      ? `\u2265 ${parseFloat(runDetail.pass_threshold).toFixed(2)}`
                      : "\u2014"}
                  </div>
                </div>
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.passRate")}</div>
                  <div className="eval-reports-continuous__detail-value">
                    {runDetail.pass_rate ? parseFloat(runDetail.pass_rate).toFixed(2) : '—'}
                  </div>
                </div>
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.sessions")}</div>
                  <div className="eval-reports-continuous__detail-value">
                    {runDetail.summary?.sessions || runDetail.summary?.unique_sessions || runDetail.summary?.total_sessions || 0}
                  </div>
                </div>
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.evaluations")}</div>
                  <div className="eval-reports-continuous__detail-value">
                    {runDetail.summary?.total_evaluations || 0}
                  </div>
                </div>
                <div>
                  <div className="eval-reports-continuous__detail-label">{t("continuous.hasReport")}</div>
                  <div className="eval-reports-continuous__detail-value">
                    {(runDetail.has_report || runDetail.s3_location) ? t("continuous.yes") : t("continuous.no")}
                  </div>
                </div>
              </div>

              <div className="eval-reports-continuous__detail-section">
                <div className="eval-reports-continuous__detail-label">{t("continuous.timeWindow")}</div>
                <div className="eval-reports-continuous__detail-timewindow">
                  <div className="eval-reports-continuous__detail-timewindow-item">
                    <span className="eval-reports-continuous__detail-timewindow-label">{t("continuous.start")}</span>
                    <span className="eval-reports-continuous__detail-timewindow-value">
                      {runDetail.time_window?.start || "\u2014"}
                    </span>
                  </div>
                  <span className="eval-reports-continuous__detail-timewindow-arrow">&rarr;</span>
                  <div className="eval-reports-continuous__detail-timewindow-item">
                    <span className="eval-reports-continuous__detail-timewindow-label">{t("continuous.end")}</span>
                    <span className="eval-reports-continuous__detail-timewindow-value">
                      {runDetail.time_window?.end || "\u2014"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="eval-reports-continuous__detail-section">
                <div className="eval-reports-continuous__detail-label">{t("continuous.timestamps")}</div>
                <div className="eval-reports-continuous__detail-timestamps">
                  <span>{t("continuous.createdLabel")} <code className="eval-reports-continuous__code eval-reports-continuous__code--small">{runDetail.created_at || "\u2014"}</code></span>
                  <span>{t("continuous.finishedLabel")} <code className="eval-reports-continuous__code eval-reports-continuous__code--small">{runDetail.finished_at || "\u2014"}</code></span>
                </div>
              </div>



              {(runDetail.metrics) && (
                <div className="eval-reports-continuous__detail-section">
                  <div className="eval-reports-continuous__detail-label">{t("continuous.metrics")}</div>
                  <div className="eval-reports-continuous__metrics-tags">
                    {runDetail.metrics.split(",").map((m: string) => (
                      <span key={m.trim()} className="eval-reports-continuous__metric-tag">
                        {m.trim().replace("Builtin.", "")}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ─── Inline Report (collapsed by default) ─── */}
            {/* ─── Run-level Radar Chart (aggregated evaluator scores) ─── */}
            {runDetail.evaluator_summary && Object.keys(runDetail.evaluator_summary).length >= 3 && (
              <div className="eval-reports-continuous__card">
                <h3 className="eval-reports-continuous__section-title">
                  {t("continuous.scoreOverview")}
                  <AskEvalAgent question={t("askAgent.scoreOverview", { runId: selectedRun })} />
                </h3>
                <div style={{display:"flex",alignItems:"center",gap:"24px",padding:"12px 0",flexWrap:"wrap"}}>
                  <RadarChart
                    scores={Object.fromEntries(
                      Object.entries(runDetail.evaluator_summary)
                      .filter(([k]) => !runDetail.metrics || runDetail.metrics.split(",").map((m: string) => m.trim()).includes(k))
                      .map(([k, v]: [string, any]) => [k, v?.avg_score || 0])
                    )}
                    size={260}
                    thresholds={metricThresholds(runDetail.evaluator_summary)}
                    title={t("continuous.aggregatedScores")}
                  />
                  <div style={{flex:1,minWidth:"200px"}}>
                    <div style={{fontSize:"11px",color:"var(--eval-text-secondary)",marginBottom:"8px"}}>{t("continuous.perEvaluator")}</div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
                      <thead>
                        <tr style={{borderBottom:"1px solid var(--eval-border)"}}>
                          <th style={{textAlign:"left",padding:"4px 8px",color:"var(--eval-text-secondary)",fontSize:"10px"}}>{t("continuous.colEvaluator")}</th>
                          <th style={{textAlign:"center",padding:"4px 8px",color:"var(--eval-text-secondary)",fontSize:"10px"}}>{t("continuous.colAvg")}</th>
                          <th style={{textAlign:"center",padding:"4px 8px",color:"var(--eval-text-secondary)",fontSize:"10px"}}>{t("continuous.colCount")}</th>
                          <th style={{textAlign:"center",padding:"4px 8px",color:"var(--eval-text-secondary)",fontSize:"10px"}}>{t("continuous.colPassRate")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(runDetail.evaluator_summary)
                        .filter(([name]) => !runDetail.metrics || runDetail.metrics.split(",").map((m: string) => m.trim()).includes(name))
                        .map(([name, data]: [string, any]) => {
                          const thr = metricThreshold(data);
                          return (
                          <tr key={name} style={{borderBottom:"1px solid var(--eval-border)"}}>
                            <td style={{padding:"6px 8px",fontWeight:500}}>
                              {name}
                              {thr != null && (
                                <span style={{marginLeft:4,fontWeight:400,color:"var(--eval-text-secondary)"}}>
                                  {t("continuous.metricThreshold", { value: thr.toFixed(2) })}
                                </span>
                              )}
                              <AskEvalAgent question={t("askAgent.evaluator", {
                                runId: selectedRun,
                                evaluator: name,
                                avg: askValue(data?.avg_score, 3),
                                passRate: data?.pass_rate != null ? `${(data.pass_rate * 100).toFixed(0)}%` : "—",
                              })} />
                            </td>
                            <td style={{padding:"6px 8px",textAlign:"center",fontWeight:700,color: metricAvgColor(data?.avg_score || 0, thr)}}>
                              {(data?.avg_score || 0).toFixed(3)}
                            </td>
                            <td style={{padding:"6px 8px",textAlign:"center",color:"var(--eval-text-secondary)"}}>{data?.count || 0}</td>
                            <td style={{padding:"6px 8px",textAlign:"center",color:"var(--eval-text-secondary)"}}>{data?.pass_rate != null ? `${(data.pass_rate * 100).toFixed(0)}%` : "—"}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {/* ─── AI review of the failed evaluations (all verdicts, not only Failure Cases) ─── */}
            {selectedRun && <AiReviewSection requestId={selectedRun} agent={agent} />}

            {(sessions.length > 0 || sessionsLoading) && (
              <div ref={sessionsRef} className="eval-reports-continuous__card">
                <h3 className="eval-reports-continuous__section-title">
                  {t("continuous.sessionsTitle", { count: sessionsLoading ? "..." : (() => {
                    const filtered = sessions
                      .filter(s => !sessionFilter.status || (s.status || "").toUpperCase() === sessionFilter.status)
                      .filter(s => !sessionFilter.lowestMetric || s.lowest_score_evaluator === sessionFilter.lowestMetric)
                      .filter(s => !sessionFilter.minScore || (parseFloat(s.avg_score) || 0) >= parseFloat(sessionFilter.minScore))
                      .filter(s => !sessionFilter.maxScore || (parseFloat(s.avg_score) || 0) <= parseFloat(sessionFilter.maxScore))
                      .filter(s => !sessionFilter.search || (s.session_id || "").toLowerCase().includes(sessionFilter.search.toLowerCase()));
                    return `${filtered.length}${filtered.length !== sessions.length ? ` / ${sessions.length}` : ""}`;
                  })() })}
                  <AskEvalAgent question={t("askAgent.sessions", { runId: selectedRun })} />
                </h3>
                {/* Session Filters */}
                {sessions.length > 0 && (
                  <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginBottom:"12px",padding:"0 4px"}}>
                    <input
                      type="text"
                      placeholder={t("continuous.searchSession")}
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)",width:"180px"}}
                      value={sessionFilter.search}
                      onChange={(e) => { setSessionFilter({...sessionFilter, search: e.target.value}); setSessionPage(0); }}
                    />
                    <select
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)"}}
                      value={sessionFilter.status}
                      onChange={(e) => { setSessionFilter({...sessionFilter, status: e.target.value}); setSessionPage(0); }}
                    >
                      <option value="">{t("continuous.allStatus")}</option>
                      {[...new Set(sessions.map(s => (s.status || "").toUpperCase()))].sort().map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                    <select
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)"}}
                      value={sessionFilter.lowestMetric}
                      onChange={(e) => { setSessionFilter({...sessionFilter, lowestMetric: e.target.value}); setSessionPage(0); }}
                    >
                      <option value="">{t("continuous.allMetrics")}</option>
                      {[...new Set(sessions.map(s => s.lowest_score_evaluator).filter(Boolean))].sort().map(m => (
                        <option key={m} value={m}>{(m || "").replace("Builtin.", "")}</option>
                      ))}
                    </select>
                    <input
                      type="number" step="0.1" min="0" max="1" placeholder="Min Score"
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)",width:"90px"}}
                      value={sessionFilter.minScore}
                      onChange={(e) => { setSessionFilter({...sessionFilter, minScore: e.target.value}); setSessionPage(0); }}
                    />
                    <input
                      type="number" step="0.1" min="0" max="1" placeholder="Max Score"
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)",width:"90px"}}
                      value={sessionFilter.maxScore}
                      onChange={(e) => { setSessionFilter({...sessionFilter, maxScore: e.target.value}); setSessionPage(0); }}
                    />
                    {(sessionFilter.status || sessionFilter.lowestMetric || sessionFilter.minScore || sessionFilter.maxScore || sessionFilter.search) && (
                      <button
                        className="eval-reports-continuous__btn eval-reports-continuous__btn--small"
                        onClick={() => { setSessionFilter({ status: "", lowestMetric: "", minScore: "", maxScore: "", search: "" }); setSessionPage(0); }}
                      >
                        {t("continuous.clear")}
                      </button>
                    )}
                  </div>
                )}
                <div className="eval-reports-continuous__table-wrapper">
                <table className="eval-reports-continuous__table">
                  <thead>
                    <tr>
                      <th className="eval-reports-continuous__th" style={{width:"32px"}}></th>
                      <th className="eval-reports-continuous__th">{t("continuous.colSessionId")}</th>
                      <th className="eval-reports-continuous__th" style={{cursor:"pointer",userSelect:"none"}} onClick={() => { setSessionSort(prev => prev.field === "avg_score" ? {field:"avg_score", dir: prev.dir === "asc" ? "desc" : "asc"} : {field:"avg_score", dir:"desc"}); setSessionPage(0); }}>{t("continuous.colAvgScore")} {sessionSort.field === "avg_score" ? (sessionSort.dir === "asc" ? "▲" : "▼") : ""}</th>
                      <th className="eval-reports-continuous__th">{t("continuous.colStatus")}</th>
                      <th className="eval-reports-continuous__th" style={{cursor:"pointer",userSelect:"none"}} onClick={() => { setSessionSort(prev => prev.field === "lowest_score" ? {field:"lowest_score", dir: prev.dir === "asc" ? "desc" : "asc"} : {field:"lowest_score", dir:"asc"}); setSessionPage(0); }}>{t("continuous.colLowestScore")} {sessionSort.field === "lowest_score" ? (sessionSort.dir === "asc" ? "▲" : "▼") : ""}</th>
                      <th className="eval-reports-continuous__th">{t("continuous.colLowestMetric")}</th>
                      <th className="eval-reports-continuous__th">{t("continuous.colEvaluatedAt")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionsLoading ? (
                      <tr><td colSpan={7} className="eval-reports-continuous__td" style={{textAlign:"center",padding:"20px"}}>{t("continuous.loadingSessions")}</td></tr>
                    ) : (() => { const filtered = sessions
                      .filter(s => !sessionFilter.status || (s.status || "").toUpperCase() === sessionFilter.status)
                      .filter(s => !sessionFilter.lowestMetric || s.lowest_score_evaluator === sessionFilter.lowestMetric)
                      .filter(s => !sessionFilter.minScore || (parseFloat(s.avg_score) || 0) >= parseFloat(sessionFilter.minScore))
                      .filter(s => !sessionFilter.maxScore || (parseFloat(s.avg_score) || 0) <= parseFloat(sessionFilter.maxScore))
                      .filter(s => !sessionFilter.search || (s.session_id || "").toLowerCase().includes(sessionFilter.search.toLowerCase()));
                      const sorted = [...filtered].sort((a, b) => { if (!sessionSort.field) return 0; const aV = parseFloat(a[sessionSort.field]) || 0; const bV = parseFloat(b[sessionSort.field]) || 0; return sessionSort.dir === "asc" ? aV - bV : bV - aV; }); return sorted.slice(sessionPage * SESSION_PAGE_SIZE, (sessionPage + 1) * SESSION_PAGE_SIZE); })().map((s: any, i: number) => (<React.Fragment key={`${s.session_id}-${i}`}>
                      <tr className="eval-reports-continuous__tr" style={{cursor:"pointer"}} onClick={() => viewSessionDetail(s.session_id)}>
                        <td className="eval-reports-continuous__td" style={{width:"32px",textAlign:"center",fontSize:"16px",fontWeight:700,color:"var(--eval-active-color)"}}>
                          {selectedSession === s.session_id ? "−" : "+"}
                        </td>
                        <td className="eval-reports-continuous__td" style={{whiteSpace:"normal",wordBreak:"break-all"}}>
                          <code className="eval-reports-continuous__code" style={{overflow:"visible",textOverflow:"unset",maxWidth:"none",whiteSpace:"normal",wordBreak:"break-all"}}>
                            {s.session_id}
                          </code>
                          <AskEvalAgent question={t("askAgent.session", {
                            runId: selectedRun,
                            sessionId: s.session_id,
                            score: askValue(s.avg_score, 3),
                            metric: (s.lowest_score_evaluator || "—").replace("Builtin.", ""),
                            lowest: askValue(s.lowest_score, 3),
                          })} />
                        </td>
                        <td className="eval-reports-continuous__td">
                          <ScoreBadge score={s.avg_score} threshold={runDetail?.pass_threshold} />
                        </td>
                        <td className="eval-reports-continuous__td">
                          <StatusBadge status={s.status?.toUpperCase()} />
                        </td>
                        <td className="eval-reports-continuous__td">
                          <ScoreBadge score={s.lowest_score} threshold={runDetail?.pass_threshold} />
                        </td>
                        <td className="eval-reports-continuous__td">
                          <span className="eval-reports-continuous__metric-tag">
                            {(s.lowest_score_evaluator || "").replace("Builtin.", "")}
                          </span>
                        </td>
                        <td className="eval-reports-continuous__td">
                          <span className="eval-reports-continuous__code eval-reports-continuous__code--small">
                            {s.evaluated_at?.slice(0, 19) || "\u2014"}
                          </span>
                        </td>
                      </tr>
                      {selectedSession === s.session_id && (
                        <tr key={`${s.session_id}-detail`}>
                          <td colSpan={7} style={{padding:"16px 20px",background:"var(--eval-hover-bg, #1e1e3a)",maxWidth:"0",overflow:"hidden"}}>
                            {sessionDetailLoading ? (
                              <p style={{color:"var(--eval-text-secondary)",fontSize:"13px"}}>{t("continuous.loadingSessionDetail")}</p>
                            ) : sessionDetail?.error ? (
                              <p style={{color:"#ef4444",fontSize:"13px"}}>{sessionDetail.error}</p>
                            ) : sessionDetail ? (
                              <div>
                                {/* Evaluator Scores */}
                                {/* Session-level Radar Chart */}
                                {sessionDetail.evaluation?.evaluator_scores && Object.keys(sessionDetail.evaluation.evaluator_scores).length >= 1 && (
                                  <div style={{marginBottom:"16px",padding:"12px",background:"var(--eval-surface)",borderRadius:"8px",border:"1px solid var(--eval-border)",display:"flex",justifyContent:"center"}}>
                                    <RadarChart
                                      scores={sessionDetail.evaluation.evaluator_scores}
                                      size={200}
                                      thresholds={metricThresholds(runDetail?.evaluator_summary)}
                                      showValues={true}
                                      title={t("continuous.sessionScore", { score: sessionDetail.evaluation?.avg_score?.toFixed(3) || "—" })}
                                    />
                                  </div>
                                )}
                                <h4 style={{margin:"0 0 12px",fontSize:"14px",color:"var(--eval-text-primary)"}}>
                                  {t("continuous.evaluatorScores")}
                                </h4>
                                <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:"12px",marginBottom:"16px",maxWidth:"100%",overflow:"hidden"}}>
                                  {Object.entries(sessionDetail.evaluation?.evaluator_details || {}).map(([evaluator, details]: [string, any]) => {
                                    const detailList = Array.isArray(details) ? details : [details];
                                    return detailList.map((d: any, di: number) => (
                                      <div key={`${evaluator}-${di}`} style={{background:"var(--eval-surface)",border:"1px solid var(--eval-border)",borderRadius:"8px",padding:"12px",overflow:"hidden",minWidth:0}}>
                                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"6px"}}>
                                          <span style={{fontWeight:600,fontSize:"13px",color:"var(--eval-text-primary)"}}>
                                            {evaluator.replace("Builtin.", "")}
                                          </span>
                                          <span style={{
                                            fontSize:"12px",
                                            fontWeight:700,
                                            padding:"2px 8px",
                                            borderRadius:"4px",
                                            background: d.score >= 0.7 ? "rgba(34,197,94,0.15)" : d.score >= 0.5 ? "rgba(234,179,8,0.15)" : "rgba(239,68,68,0.15)",
                                            color: d.score >= 0.7 ? "#22c55e" : d.score >= 0.5 ? "#eab308" : "#ef4444",
                                          }}>
                                            {d.score.toFixed(2)}{d.label ? ` · ${d.label}` : ""}
                                          </span>
                                        </div>
                                        {d.explanation && (
                                          <p style={{margin:0,fontSize:"12px",color:"var(--eval-text-secondary)",lineHeight:"1.5",maxHeight:"80px",overflow:"auto"}}>
                                            {d.explanation}
                                          </p>
                                        )}
                                      </div>
                                    ));
                                  })}
                                </div>

                                {/* Conversation — Tool Trace View */}
                                {sessionDetail.conversation?.length > 0 && (
                                  <ConversationTrace messages={sessionDetail.conversation} />
                                )}
                                {(!sessionDetail.conversation || sessionDetail.conversation.length === 0) && (
                                  <p style={{color:"var(--eval-text-secondary)",fontSize:"12px",margin:"8px 0 0"}}>
                                    {t("continuous.noConversation")}
                                  </p>
                                )}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>))}
                  </tbody>
                </table>
                </div>
                {/* Pagination */}
                {(() => { const filteredCount = sessions
                  .filter(s => !sessionFilter.status || (s.status || "").toUpperCase() === sessionFilter.status)
                  .filter(s => !sessionFilter.lowestMetric || s.lowest_score_evaluator === sessionFilter.lowestMetric)
                  .filter(s => !sessionFilter.minScore || (parseFloat(s.avg_score) || 0) >= parseFloat(sessionFilter.minScore))
                  .filter(s => !sessionFilter.maxScore || (parseFloat(s.avg_score) || 0) <= parseFloat(sessionFilter.maxScore))
                  .filter(s => !sessionFilter.search || (s.session_id || "").toLowerCase().includes(sessionFilter.search.toLowerCase())).length;
                  return filteredCount > SESSION_PAGE_SIZE ? (
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",fontSize:"13px",color:"var(--eval-text-secondary)"}}>
                    <span>{t(filteredCount !== sessions.length ? "continuous.sessionsPageFiltered" : "continuous.sessionsPage", { page: sessionPage + 1, pages: Math.ceil(filteredCount / SESSION_PAGE_SIZE), n: filteredCount, total: sessions.length })}</span>
                    <div style={{display:"flex",gap:"8px"}}>
                      <button className="eval-reports-continuous__btn" disabled={sessionPage === 0} onClick={() => setSessionPage(p => p - 1)}>{t("continuous.prev")}</button>
                      <button className="eval-reports-continuous__btn" disabled={(sessionPage + 1) * SESSION_PAGE_SIZE >= filteredCount} onClick={() => setSessionPage(p => p + 1)}>{t("continuous.next")}</button>
                    </div>
                  </div>
                ) : null; })()}
              </div>
            )}


            {/* ─── Evaluations (flat list) ─── */}
            <div className="eval-reports-continuous__card">
              <h3 className="eval-reports-continuous__section-title">
                {t("continuous.evaluationsTitle", { count: evaluationsLoading ? "..." : evaluations.filter(ev => !evalFilter.evaluator || ev.evaluator === evalFilter.evaluator).filter(ev => !evalFilter.session_id || ev.session_id === evalFilter.session_id).filter(ev => !evalFilter.level || ev.level === evalFilter.level).filter(ev => !evalFilter.minScore || ev.score >= parseFloat(evalFilter.minScore)).filter(ev => !evalFilter.maxScore || ev.score <= parseFloat(evalFilter.maxScore)).length })}
                <AskEvalAgent question={t("askAgent.evaluations", { runId: selectedRun })} />
              </h3>

              {evaluationsLoading ? (
                <div style={{display:"flex",alignItems:"center",gap:"8px",padding:"20px",color:"var(--eval-text-secondary)"}}>
                  <IconSpinner /> {t("continuous.loadingEvaluations")}
                </div>
              ) : evaluationsMessage && evaluations.length === 0 ? (
                <p style={{color:"var(--eval-text-secondary)",fontSize:"13px",padding:"16px 0"}}>{loadErrorText(evaluationsMessage, t)}</p>
              ) : evaluations.length > 0 ? (
                <>
                  {/* Filters */}
                  <div style={{display:"flex",gap:"8px",flexWrap:"wrap",marginBottom:"12px",padding:"0 4px"}}>
                    <select
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)"}}
                      value={evalFilter.evaluator}
                      onChange={(e) => setEvalFilter({...evalFilter, evaluator: e.target.value})}
                    >
                      <option value="">{t("continuous.allEvaluators")}</option>
                      {[...new Set(evaluations.map(ev => ev.evaluator))].sort().map(name => (
                        <option key={name} value={name}>{name.replace("Builtin.", "")}</option>
                      ))}
                    </select>
                    <select
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)"}}
                      value={evalFilter.session_id}
                      onChange={(e) => setEvalFilter({...evalFilter, session_id: e.target.value})}
                    >
                      <option value="">{t("continuous.allSessions")}</option>
                      {[...new Set(evaluations.map(ev => ev.session_id))].sort().map(sid => (
                        <option key={sid} value={sid}>{sid.length > 30 ? sid.slice(0,30) + "..." : sid}</option>
                      ))}
                    </select>
                    <select
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)"}}
                      value={evalFilter.level}
                      onChange={(e) => setEvalFilter({...evalFilter, level: e.target.value})}
                    >
                      <option value="">{t("continuous.allLevels")}</option>
                      <option value="Trace">{t("continuous.levelTrace")}</option>
                      <option value="Span">{t("continuous.levelSpan")}</option>
                      <option value="Session">{t("continuous.levelSession")}</option>
                    </select>
                    <input
                      type="number" step="0.1" min="0" max="1" placeholder="Min Score"
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)",width:"90px"}}
                      value={evalFilter.minScore}
                      onChange={(e) => setEvalFilter({...evalFilter, minScore: e.target.value})}
                    />
                    <input
                      type="number" step="0.1" min="0" max="1" placeholder="Max Score"
                      style={{fontSize:"12px",padding:"4px 8px",borderRadius:"6px",border:"1px solid var(--eval-border)",background:"var(--eval-surface)",color:"var(--eval-text-primary)",width:"90px"}}
                      value={evalFilter.maxScore}
                      onChange={(e) => setEvalFilter({...evalFilter, maxScore: e.target.value})}
                    />
                    {(evalFilter.evaluator || evalFilter.session_id || evalFilter.level || evalFilter.minScore || evalFilter.maxScore) && (
                      <button
                        className="eval-reports-continuous__btn eval-reports-continuous__btn--small"
                        onClick={() => setEvalFilter({ evaluator: "", session_id: "", level: "", minScore: "", maxScore: "" })}
                      >
                        {t("continuous.clear")}
                      </button>
                    )}
                  </div>

                  {/* Table */}
                  <div className="eval-reports-continuous__table-wrapper">
                  <table className="eval-reports-continuous__table">
                    <thead>
                      <tr>
                        <th className="eval-reports-continuous__th" style={{cursor:"pointer",userSelect:"none"}} onClick={() => { setEvalSort(prev => prev.field === "evaluator" ? {field:"evaluator", dir: prev.dir === "asc" ? "desc" : "asc"} : {field:"evaluator", dir:"desc"}); setEvalPage(0); }}>{t("continuous.colEvaluator")} {evalSort.field === "evaluator" ? (evalSort.dir === "asc" ? "▲" : "▼") : ""}</th>
                        <th className="eval-reports-continuous__th">{t("continuous.colSession")}</th>
                        <th className="eval-reports-continuous__th" style={{cursor:"pointer",userSelect:"none"}} onClick={() => { setEvalSort(prev => prev.field === "level" ? {field:"level", dir: prev.dir === "asc" ? "desc" : "asc"} : {field:"level", dir:"desc"}); setEvalPage(0); }}>{t("continuous.colLevel")} {evalSort.field === "level" ? (evalSort.dir === "asc" ? "▲" : "▼") : ""}</th>
                        <th className="eval-reports-continuous__th" style={{cursor:"pointer",userSelect:"none"}} onClick={() => { setEvalSort(prev => prev.field === "score" ? {field:"score", dir: prev.dir === "asc" ? "desc" : "asc"} : {field:"score", dir:"desc"}); setEvalPage(0); }}>{t("continuous.colScore")} {evalSort.field === "score" ? (evalSort.dir === "asc" ? "▲" : "▼") : ""}</th>
                        <th className="eval-reports-continuous__th" style={{cursor:"pointer",userSelect:"none"}} onClick={() => { setEvalSort(prev => prev.field === "label" ? {field:"label", dir: prev.dir === "asc" ? "desc" : "asc"} : {field:"label", dir:"desc"}); setEvalPage(0); }}>{t("continuous.colLabel")} {evalSort.field === "label" ? (evalSort.dir === "asc" ? "▲" : "▼") : ""}</th>
                        <th className="eval-reports-continuous__th">{t("continuous.colAnalysis")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => { const filtered = evaluations
                        .filter(ev => !evalFilter.evaluator || ev.evaluator === evalFilter.evaluator)
                        .filter(ev => !evalFilter.session_id || ev.session_id === evalFilter.session_id)
                        .filter(ev => !evalFilter.level || ev.level === evalFilter.level)
                        .filter(ev => !evalFilter.minScore || ev.score >= parseFloat(evalFilter.minScore))
                        .filter(ev => !evalFilter.maxScore || ev.score <= parseFloat(evalFilter.maxScore));
                        const sorted = [...filtered].sort((a, b) => { if (!evalSort.field) return 0; const aV = a[evalSort.field]; const bV = b[evalSort.field]; if (typeof aV === "number" && typeof bV === "number") return evalSort.dir === "asc" ? aV - bV : bV - aV; const aS = String(aV || ""); const bS = String(bV || ""); return evalSort.dir === "asc" ? aS.localeCompare(bS) : bS.localeCompare(aS); });
                        const paged = sorted.slice(evalPage * EVAL_PAGE_SIZE, (evalPage + 1) * EVAL_PAGE_SIZE);
                        return paged; })().map((ev, idx) => (
                        <React.Fragment key={idx}>
                        <tr className="eval-reports-continuous__tr" style={{cursor:"pointer"}} onClick={() => setExpandedEvalIdx(expandedEvalIdx === idx ? null : idx)}>
                          <td className="eval-reports-continuous__td">
                            <span style={{marginRight:4,fontSize:10,opacity:0.5}}>{expandedEvalIdx === idx ? "▼" : "▶"}</span>
                            <span className="eval-reports-continuous__metric-tag">{ev.evaluator.replace("Builtin.", "")}</span>
                            <AskEvalAgent question={t("askAgent.evaluation", {
                              runId: selectedRun,
                              evaluator: ev.evaluator.replace("Builtin.", ""),
                              sessionId: ev.session_id || "—",
                              traceId: ev.trace_id || "—",
                              score: askValue(ev.score),
                              label: ev.label || "—",
                            })} />
                          </td>
                          <td className="eval-reports-continuous__td" style={{whiteSpace:"normal",wordBreak:"break-all"}}>
                            <code className="eval-reports-continuous__code eval-reports-continuous__code--small" style={{overflow:"visible",textOverflow:"unset",maxWidth:"none",whiteSpace:"normal",wordBreak:"break-all"}} title={ev.session_id}>
                              {ev.session_id}
                            </code>
                          </td>
                          <td className="eval-reports-continuous__td">
                            <span style={{fontSize:"11px",color:"var(--eval-text-secondary)"}}>{ev.level}</span>
                          </td>
                          <td className="eval-reports-continuous__td">
                            <ScoreBadge score={ev.score} threshold={runDetail?.pass_threshold} />
                          </td>
                          <td className="eval-reports-continuous__td">
                            <span style={{fontSize:"12px"}}>{ev.label || "\u2014"}</span>
                          </td>
                          <td className="eval-reports-continuous__td">
                            {ev.analysis ? (
                              <span style={{
                                fontSize:"11px",
                                padding:"2px 6px",
                                borderRadius:"4px",
                                background: ev.analysis.verdict === "Real Issue" ? "rgba(239,68,68,0.15)" : ev.analysis.verdict === "False Positive" ? "rgba(34,197,94,0.15)" : "rgba(234,179,8,0.15)",
                                color: ev.analysis.verdict === "Real Issue" ? "#ef4444" : ev.analysis.verdict === "False Positive" ? "#22c55e" : "#eab308",
                              }} title={ev.analysis.root_cause}>
                                {ev.analysis.verdict}
                              </span>
                            ) : (
                              <span style={{color:"var(--eval-text-secondary)",fontSize:"11px"}}>{"\u2014"}</span>
                            )}
                          </td>
                        </tr>
                        {expandedEvalIdx === idx && (
                          <tr className="eval-reports-continuous__tr">
                            <td colSpan={6} style={{padding:"12px 16px",background:"#f9fafb",borderTop:"none"}}>
                              <div style={{fontSize:12,lineHeight:1.6,maxWidth:"calc(100vw - 200px)",overflow:"auto"}}>
                                <div style={{marginBottom:8}}>
                                  <span style={{fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:0.5,color:"#6b7280"}}>{t("continuous.explanation")}</span>
                                  <p style={{margin:"4px 0",color:"#374151",whiteSpace:"pre-wrap",maxWidth:800,wordBreak:"break-word"}}>{ev.explanation || t("continuous.noExplanation")}</p>
                                </div>
                                {ev.trace_id && (
                                  <div style={{marginBottom:8,fontSize:11,color:"#9ca3af"}}>
                                    <span>{t("continuous.traceLabel")} <code>{ev.trace_id}</code></span>
                                    {ev.span_id && <span style={{marginLeft:12}}>{t("continuous.spanLabel")} <code>{ev.span_id}</code></span>}
                                    {ev.evaluation_date && <span style={{marginLeft:12}}>{t("continuous.dateLabel", { date: ev.evaluation_date })}</span>}
                                  </div>
                                )}
                                {ev.analysis && (
                                  <div style={{marginTop:12,padding:"10px 12px",background:"#fff",border:"1px solid #e5e7eb",borderRadius:6}}>
                                    <span style={{fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:0.5,color:"#6b7280"}}>{t("continuous.aiAnalysis")}</span>
                                    <div style={{marginTop:6,display:"flex",gap:8,flexWrap:"wrap"}}>
                                      <span style={{padding:"2px 6px",borderRadius:4,fontSize:11,fontWeight:700,background:ev.analysis.severity === "critical" ? "rgba(220,38,38,0.1)" : ev.analysis.severity === "high" ? "rgba(234,88,12,0.1)" : "rgba(107,114,128,0.1)",color:ev.analysis.severity === "critical" ? "#dc2626" : ev.analysis.severity === "high" ? "#ea580c" : "#6b7280"}}>{ev.analysis.severity}</span>
                                      <span style={{padding:"2px 6px",borderRadius:4,fontSize:11,fontWeight:700,background:"rgba(37,99,235,0.1)",color:"#2563eb"}}>{ev.analysis.action}</span>
                                    </div>
                                    <div style={{marginTop:8}}>
                                      <div style={{fontSize:11,fontWeight:600,color:"#6b7280"}}>{t("continuous.rootCause")}</div>
                                      <p style={{margin:"2px 0",fontSize:12,color:"#374151"}}>{ev.analysis.root_cause}</p>
                                    </div>
                                    <div style={{marginTop:6}}>
                                      <div style={{fontSize:11,fontWeight:600,color:"#6b7280"}}>{t("continuous.suggestion")}</div>
                                      <p style={{margin:"2px 0",fontSize:12,color:"#374151"}}>{ev.analysis.suggestion}</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* Evaluations Pagination */}
                  {(() => { const total = evaluations
                    .filter(ev => !evalFilter.evaluator || ev.evaluator === evalFilter.evaluator)
                    .filter(ev => !evalFilter.session_id || ev.session_id === evalFilter.session_id)
                    .filter(ev => !evalFilter.level || ev.level === evalFilter.level)
                    .filter(ev => !evalFilter.minScore || ev.score >= parseFloat(evalFilter.minScore))
                    .filter(ev => !evalFilter.maxScore || ev.score <= parseFloat(evalFilter.maxScore)).length;
                    return total > EVAL_PAGE_SIZE ? (
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",fontSize:"13px",color:"var(--eval-text-secondary)"}}>
                        <span>{t("continuous.evaluationsPage", { page: evalPage + 1, pages: Math.ceil(total / EVAL_PAGE_SIZE), n: total })}</span>
                        <div style={{display:"flex",gap:"8px"}}>
                          <button className="eval-reports-continuous__btn" disabled={evalPage === 0} onClick={() => setEvalPage(p => p - 1)}>{t("continuous.prev")}</button>
                          <button className="eval-reports-continuous__btn" disabled={(evalPage + 1) * EVAL_PAGE_SIZE >= total} onClick={() => setEvalPage(p => p + 1)}>{t("continuous.next")}</button>
                        </div>
                      </div>
                    ) : null; })()}
                  <div style={{padding:"8px 4px",fontSize:"12px",color:"var(--eval-text-secondary)"}}>
                    {t("continuous.showing", { n: evaluations
                      .filter(ev => !evalFilter.evaluator || ev.evaluator === evalFilter.evaluator)
                      .filter(ev => !evalFilter.session_id || ev.session_id === evalFilter.session_id)
                      .filter(ev => !evalFilter.level || ev.level === evalFilter.level)
                      .filter(ev => !evalFilter.minScore || ev.score >= parseFloat(evalFilter.minScore))
                      .filter(ev => !evalFilter.maxScore || ev.score <= parseFloat(evalFilter.maxScore))
                      .length, total: evaluations.length })}
                  </div>
                </>
              ) : null}
            </div>

            {/* ─── Evaluation Report (iframe) — last on the page: it is the
                 full generated artifact, read after the summaries above ─── */}
            {(inlineReportUrl || reportLoading) && (
              <div ref={reportRef} className="eval-reports-continuous__card">
                <h3 className="eval-reports-continuous__section-title">
                  {t("continuous.reportTitle")}
                  <AskEvalAgent question={t("askAgent.report", { runId: selectedRun })} />
                </h3>
                {reportLoading ? (
                  <p style={{color:"var(--eval-text-secondary)",fontSize:"13px",padding:"16px 0"}}>{t("continuous.loadingReport")}</p>
                ) : inlineReportUrl ? (
                  <div style={{position:"relative"}}>
                    <div
                      className="eval-reports-continuous__iframe-wrapper"
                      style={{
                        maxHeight: reportExpanded ? "none" : "400px",
                        overflow: "hidden",
                        transition: "max-height 0.3s ease",
                      }}
                    >
                      <iframe
                        src={inlineReportUrl}
                        sandbox="allow-scripts allow-same-origin"
                        className="eval-reports-continuous__iframe"
                        style={{ height: reportExpanded ? "800px" : "400px", transition: "height 0.3s ease" }}
                        title={t("continuous.reportFrame")}
                      />
                    </div>
                    {!reportExpanded && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "80px",
                          background: "linear-gradient(transparent, var(--eval-surface, #1a1a2e))",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          paddingBottom: "12px",
                        }}
                      >
                        <button
                          className="eval-reports-continuous__btn eval-reports-continuous__btn--primary"
                          onClick={() => setReportExpanded(true)}
                        >
                          {t("continuous.showFullReport")}
                        </button>
                      </div>
                    )}
                    {reportExpanded && (
                      <div style={{textAlign:"center",padding:"12px 0"}}>
                        <button
                          className="eval-reports-continuous__btn"
                          onClick={() => setReportExpanded(false)}
                        >
                          {t("continuous.collapseReport")}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ─── Run List View (Cloudscape Table) ───
  const runListColumnDefinitions = [
    {
      id: "runId",
      header: t("continuous.colRunId"),
      sortingField: "request_id",
      minWidth: 310,
      cell: (run: any) => (
        <Link onFollow={(e: any) => { e.preventDefault(); viewDetail(run.request_id); }}>
          {run.request_id}
        </Link>
      ),
    },
    {
      id: "status",
      header: t("continuous.colStatus"),
      sortingField: "status",
      minWidth: 130,
      cell: (run: any) => {
        const s = (run.status || "").toUpperCase();
        const type = s === "SUCCEEDED" ? "success" : s === "FAILED" ? "error" : s.includes("PROGRESS") ? "in-progress" : "info";
        return <StatusIndicator type={type}>{run.status}</StatusIndicator>;
      },
    },
    {
      id: "score",
      header: t("continuous.colScore"),
      sortingField: "overall_avg_score",
      minWidth: 55,
      cell: (run: any) => {
        const score = run.overall_avg_score ?? run.score;
        const num = typeof score === "string" ? parseFloat(score) : score;
        if (num == null || isNaN(num)) return <Box color="text-status-inactive">&mdash;</Box>;
        const color = num >= 0.7 ? "text-status-success" : num >= 0.5 ? "text-status-warning" : "text-status-error";
        return <Box color={color} fontWeight="bold">{num.toFixed(2)}</Box>;
      },
    },
    {
      id: "sessions",
      header: t("continuous.colSessions"),
      sortingField: "total_sessions",
      minWidth: 60,
      cell: (run: any) => run.total_sessions ?? run.sessions ?? run.unique_sessions ?? "\u2014",
    },
    {
      id: "passRate",
      header: t("continuous.colRunPassRate"),
      sortingField: "pass_rate",
      minWidth: 70,
      cell: (run: any) => {
        const rate = run.pass_rate;
        const num = typeof rate === "string" ? parseFloat(rate) : rate;
        if (num == null || isNaN(num)) return <Box color="text-status-inactive">&mdash;</Box>;
        const color = num >= 0.9 ? "text-status-success" : num >= 0.7 ? "text-status-warning" : "text-status-error";
        return <Box color={color} fontWeight="bold">{(num * 100).toFixed(1)}%</Box>;
      },
    },
    {
      id: "timeWindow",
      header: t("continuous.colWindow"),
      sortingField: "time_window_start",
      minWidth: 120,
      cell: (run: any) => run.time_window_start ? run.time_window_start.slice(0, 10) : "\u2014",
    },

    {
      id: "created",
      header: t("continuous.colCreated"),
      sortingField: "created_at",
      minWidth: 160,
      cell: (run: any) => run.created_at ? run.created_at.slice(0, 16).replace("T", " ") : "\u2014",
    },
  ];

  return (
    <div className="eval-reports-continuous__container">

      {error && <p className="eval-reports-continuous__error">{error}</p>}

      <div className="eval-reports-continuous__card">
        <HistogramChart
          data={histogramData}
          loading={histogramLoading}
          question={t("askAgent.trend", { agent: agentLabel })}
        />
      </div>

      <Table
        {...collectionProps}
        variant="container"
        items={collectionItems}
        loading={loading}
        loadingText={t("continuous.loadingRuns")}
        columnDefinitions={runListColumnDefinitions}
        resizableColumns
        trackBy="request_id"
        pagination={<Pagination {...paginationProps} />}
        header={
          <Header
            counter={runs.length ? `(${runs.length})` : undefined}
            description={t("continuous.runsDescription")}
            actions={
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <AskEvalAgent question={t("askAgent.runs", { agent: agentLabel })} />
                <Button iconName="refresh" onClick={() => loadRuns()} loading={loading}>
                  {t("continuous.refresh")}
                </Button>
              </SpaceBetween>
            }
          >
            {t("continuous.runsHeader")}
          </Header>
        }
        filter={
          <TextFilter
            {...filterProps}
            filteringPlaceholder={t("continuous.searchRuns")}
            filteringAriaLabel={t("continuous.filterRuns")}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            <b>{t("continuous.noRuns")}</b>
            <Box variant="p" color="inherit">
              {t("continuous.noRunsBody")}
            </Box>
          </Box>
        }
        preferences={
          <CollectionPreferences
            title={t("continuous.preferences")}
            confirmLabel={t("continuous.confirm")}
            cancelLabel={t("continuous.cancel")}
            preferences={runTablePreferences}
            onConfirm={({ detail }) => setRunTablePreferences(detail)}
            pageSizePreference={{
              title: t("continuous.runsPerPage"),
              options: RUN_PAGE_SIZE_OPTIONS,
            }}
          />
        }
        footer={error ? <Box color="text-status-error">{error}</Box> : undefined}
      />
      <div style={{ height: 80 }} />
    </div>
  );
}
